import { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    FormLabel,
    Box,
    TextField,
    Autocomplete,
    Divider,
    CircularProgress,
    Avatar,
    Typography,
} from "@mui/material";
import { MuiTelInput } from "mui-tel-input";
import CommonTextField from "../common/CommonTextField";
import CommonButtonActionButton from "../common/CommonButtonActionButton";
import { useToast } from "../../hook/useToast";
import httpcommon from "../../helper/httpcommon";

const BOARD_OPTIONS = ["CBSE", "ICSE", "State Board", "Other"];
const STUDENT_CLASS_OPTIONS = ["8", "9", "10", "11", "12"];
const ADD_NEW_SCHOOL_OPTION = "__add_new_school__";

// A school the student types that isn't in the admin list is offered in the
// dropdown as an `Add "…"` row; this unwraps that row back to the plain name.
const ADD_SCHOOL_OPTION_RE = /^Add\s+"(.*)"$/;
const normalizeSchoolInput = (value) => {
    if (!value) return "";
    const str = typeof value === "string" ? value : String(value);
    const match = str.match(ADD_SCHOOL_OPTION_RE);
    return (match ? match[1] : str).trim();
};
const PROFILE_COLORS = {
    primary: "#FFB332",
    secondary: "#F7AA47",
    secondaryDark: "#E8952F",
    text: "#171717",
    muted: "#667085",
    border: "#EAECF0",
    fieldBorder: "#D8DBDF",
    surface: "#F9FAFB",
    white: "#FFFFFF",
};

const fieldLabelSx = {
    mb: 0.75,
    color: PROFILE_COLORS.text,
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: "20px",
};

const fieldSx = {
    mt: 0,
    mb: 0,
    "& .MuiOutlinedInput-root": {
        minHeight: 44,
        borderRadius: "8px",
        backgroundColor: PROFILE_COLORS.white,
        fontFamily: "Poppins",
        fontSize: 14,
        "& fieldset": {
            borderColor: PROFILE_COLORS.fieldBorder,
        },
        "&:hover fieldset": {
            borderColor: PROFILE_COLORS.secondary,
        },
        "&.Mui-focused fieldset": {
            borderColor: PROFILE_COLORS.primary,
            borderWidth: "1px",
        },
        "&.Mui-disabled": {
            backgroundColor: PROFILE_COLORS.surface,
        },
    },
    "& .MuiInputBase-input": {
        color: PROFILE_COLORS.text,
        fontFamily: "Poppins",
        fontSize: 14,
    },
    "& .MuiInputBase-input::placeholder": {
        color: PROFILE_COLORS.muted,
        opacity: 1,
    },
    "& .MuiFormHelperText-root": {
        mx: 0,
        mt: 0.5,
        fontFamily: "Poppins",
        fontSize: 12,
    },
};

const formControlSx = {
    m: 0,
};

const requiredFields = [
    "school_name",
    "board",
    "class",
    "birth_date",
];

const normalizeSchoolOptions = (payload) => {
    const schools = Array.isArray(payload?.schools) ? payload.schools : payload;
    if (!Array.isArray(schools)) return [];

    const optionsByName = new Map();
    schools
        .map((school) => {
            const name = typeof school === "string" ? school : school?.name || school?.school_name;
            const board = typeof school === "string" ? "" : school?.board || "";
            return { name: name?.trim(), board: board?.trim() || "" };
        })
        .filter((school) => school.name)
        .forEach((school) => {
            optionsByName.set(school.name.toLowerCase(), school);
        });

    return [...optionsByName.values()].sort((a, b) => a.name.localeCompare(b.name));
};

const getSchoolNameOptions = (schools) => {
    return schools
        .map((school) => school.name)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b));
};

const getAllowedBoardValue = (board) => {
    const normalizedBoard = board?.toString() || "";
    return BOARD_OPTIONS.some((option) => option.toLowerCase() === normalizedBoard.toLowerCase())
        ? BOARD_OPTIONS.find((option) => option.toLowerCase() === normalizedBoard.toLowerCase())
        : "";
};

const getStudentClassValue = (studentClass) => {
    const normalizedClass = studentClass?.toString() || "";
    return STUDENT_CLASS_OPTIONS.includes(normalizedClass) ? normalizedClass : "";
};

const StudentProfileUpdate = ({ open, onClose }) => {
    const { showToast } = useToast();

    const [formData, setFormData] = useState({
        student_name: "",
        school_name: "",
        board: "",
        board_name: "",
        class: "",
        section: "A",
        roll_number: "1",
        birth_date: "",
        contact_number: "",
        address: "",
    });

    const [errors, setErrors] = useState({});
    const [helperText, setHelperText] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [student, setStudent] = useState(null);
    const [schoolOptions, setSchoolOptions] = useState([]);
    const [isAddingNewSchool, setIsAddingNewSchool] = useState(false);

    const fetchProfileFormData = useCallback(async () => {
        try {
            setFetching(true);
            const [profileResult, schoolOptionsResult] = await Promise.allSettled([
                httpcommon.get("school/student/profile/"),
                httpcommon.get("school/student/school-options/"),
            ]);

            if (profileResult.status === "rejected") {
                throw profileResult.reason;
            }

            const profileResponse = profileResult.value;
            const schoolOptionsResponse =
                schoolOptionsResult.status === "fulfilled" ? schoolOptionsResult.value : null;
            const data = profileResponse.data;
            const options = normalizeSchoolOptions(schoolOptionsResponse?.data);
            const currentSchool = data.school || "";
            const selectedSchool = options.find(
                (school) => school.name.toLowerCase() === currentSchool.toLowerCase()
            );
            const profileBoard = data.board || "";
            const resolvedBoard = selectedSchool?.board || profileBoard;
            const allowedBoardValue = getAllowedBoardValue(resolvedBoard);
            const studentClass = data.student_class?.toString() || "";

            setSchoolOptions(options);
            setStudent(data);
            setIsAddingNewSchool(!selectedSchool && Boolean(currentSchool));
            setFormData({
                student_name: data.user_name || "",
                school_name: currentSchool,
                board: allowedBoardValue,
                board_name: "",
                class: getStudentClassValue(studentClass),
                section: data.section || "A",
                roll_number: data.roll_number || "1",
                birth_date: data.birth_date || "",
                contact_number: data.contact_number || "",
                address: data.address || "",
            });
        } catch (error) {
            if (error.response?.status !== 404) {
                showToast(error.response?.data?.error || "Error fetching data", "error");
            }
        } finally {
            setFetching(false);
        }
    }, [showToast]);

    const boardOptions = BOARD_OPTIONS;
    const selectedAdminSchool = schoolOptions.find(
        (school) => school.name.toLowerCase() === formData.school_name.toLowerCase()
    );
    const isBoardLocked = Boolean(
        !isAddingNewSchool && selectedAdminSchool && getAllowedBoardValue(selectedAdminSchool.board)
    );
    const schoolSelectOptions = [ADD_NEW_SCHOOL_OPTION, ...getSchoolNameOptions(schoolOptions)];

    // Fetch student profile when modal opens
    useEffect(() => {
        if (open) {
            fetchProfileFormData();
        }
    }, [fetchProfileFormData, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            if (name !== "school_name") {
                return { ...prev, [name]: value };
            }

            const matchedSchool = schoolOptions.find(
                (school) => school.name.toLowerCase() === value.toLowerCase()
            );
            if (!matchedSchool) {
                // Free-typed school (not in the admin list) — keep whatever
                // board / class the student already picked.
                return {
                    ...prev,
                    school_name: value,
                    board: getAllowedBoardValue(prev.board),
                    class: getStudentClassValue(prev.class),
                };
            }

            const allowedBoard = getAllowedBoardValue(matchedSchool.board || "");
            return {
                ...prev,
                school_name: value,
                board: allowedBoard,
            };
        });

        if (!value.toString().trim()) {
            setErrors((prev) => ({ ...prev, [name]: true }));
            setHelperText((prev) => ({ ...prev, [name]: `${name.replace("_", " ")} is required` }));
        } else {
            setErrors((prev) => ({ ...prev, [name]: false }));
            setHelperText((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handlePhoneChange = (value) => {
        setFormData((prev) => ({ ...prev, contact_number: value }));
    };

    const validateForm = () => {
        const newErrors = {};
        const newHelperText = {};

        requiredFields.forEach((field) => {
            if (!formData[field] || !formData[field].toString().trim()) {
                newErrors[field] = true;
                newHelperText[field] = `${field.replace("_", " ")} is required`;
            }
        });

        setErrors(newErrors);
        setHelperText(newHelperText);

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setLoading(true);
        const resolvedSchoolName = formData.school_name.trim();

        const board = formData.board || "";

        const payload = {
            school_name: resolvedSchoolName,
            board,
            // Backend requires a non-blank board_name whenever board === "Other";
            // there's no separate custom-board field, so mirror the label.
            board_name: board === "Other" ? "Other" : "",
            class: formData.class,
            section: formData.section || "",
            roll_number: formData.roll_number || "",
            birth_date: formData.birth_date,
            contact_number: formData.contact_number,
            address: formData.address,
            student_class: formData.class,
        };

        try {
            const response = await httpcommon.post(
                "school/student/profile/",
                payload,
                {
                    headers: { "Content-Type": "application/json" },
                }
            );

            if (response.status === 200) {
                showToast("Profile updated successfully!", "success");
                onClose();
            } else {
                showToast("Update failed. Please try again.", "error");
            }
        } catch (error) {
            showToast(error?.response?.data?.error || "Update failed", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setLoading(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "18px",
                    overflow: "hidden",
                    boxShadow: "0px 16px 75px rgba(0, 0, 0, 0.08)",
                    backgroundColor: PROFILE_COLORS.white,
                },
            }}
        >
            <DialogTitle
                sx={{
                    px: { xs: 2.5, sm: 4 },
                    py: 2.5,
                    backgroundColor:"#FFB332",
                    color: PROFILE_COLORS.white,
                    fontFamily: "Montserrat",
                    fontSize: 20,
                    fontWeight: 700,
                    lineHeight: "24px",
                }}
            >
                Personal Info
            </DialogTitle>
            <DialogContent sx={{ p: 0, backgroundColor: PROFILE_COLORS.white }}>
                <Box
                    sx={{
                        px: { xs: 2.5, sm: 4 },
                        py: 3,
                        borderBottom: `1px solid ${PROFILE_COLORS.border}`,
                        backgroundColor: PROFILE_COLORS.surface,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Avatar
                            src={student?.avatar}
                            alt={student?.student_name || student?.user_name}
                            sx={{
                                width: 78,
                                height: 78,
                                border: `3px solid ${PROFILE_COLORS.white}`,
                                backgroundColor: "#BDBDBD",
                                color: PROFILE_COLORS.white,
                                fontFamily: "Montserrat",
                                fontWeight: 700,
                            }}
                        />
                        <Box minWidth={0}>
                            <Typography
                                sx={{
                                    color: PROFILE_COLORS.text,
                                    fontFamily: "Montserrat",
                                    fontSize: 18,
                                    fontWeight: 700,
                                    lineHeight: "24px",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {student?.user_name || "Student Name"}
                            </Typography>
                            <Typography
                                sx={{
                                    mt: 0.5,
                                    color: PROFILE_COLORS.muted,
                                    fontFamily: "Montserrat",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    lineHeight: "18px",
                                    overflowWrap: "anywhere",
                                }}
                            >
                                {student?.user_email || "email@example.com"}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                {fetching ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            minHeight: 360,
                            p: 4,
                        }}
                    >
                        <CircularProgress sx={{ color: PROFILE_COLORS.secondary }} />
                    </Box>
                ) : (
                    <Box
                        component="form"
                        noValidate
                        sx={{
                            px: { xs: 2.5, sm: 4 },
                            py: 3,
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                            gap: 2.25,
                        }}
                    >
                        <FormControl fullWidth error={errors.school_name} sx={formControlSx}>
                            <FormLabel sx={fieldLabelSx}>
                                {isAddingNewSchool ? "New School Name" : "School Name"}
                            </FormLabel>
                            {isAddingNewSchool ? (
                                <TextField
                                    fullWidth
                                    name="school_name"
                                    value={formData.school_name}
                                    onChange={handleChange}
                                    placeholder="Enter your school name"
                                    error={!!errors.school_name}
                                    helperText={helperText.school_name || "This school will be added to your profile."}
                                    sx={fieldSx}
                                />
                            ) : (
                                <Autocomplete
                                    fullWidth
                                    options={schoolSelectOptions}
                                    value={formData.school_name || null}
                                    getOptionLabel={(option) =>
                                        option === ADD_NEW_SCHOOL_OPTION ? "Add a new school" : option
                                    }
                                    filterOptions={(options, params) => {
                                        const search = params.inputValue.trim().toLowerCase();
                                        const schools = options.filter((option) =>
                                            option !== ADD_NEW_SCHOOL_OPTION && option.toLowerCase().includes(search)
                                        );
                                        return [ADD_NEW_SCHOOL_OPTION, ...schools];
                                    }}
                                    renderOption={(props, option) => {
                                        if (option === ADD_NEW_SCHOOL_OPTION) {
                                            return (
                                                <li {...props}>
                                                    <Box
                                                        sx={{
                                                            width: "100%",
                                                            px: 0.5,
                                                            py: 0.75,
                                                            color: PROFILE_COLORS.secondaryDark,
                                                            fontFamily: "Poppins",
                                                            fontWeight: 700,
                                                            borderBottom: `1px dashed ${PROFILE_COLORS.secondary}`,
                                                        }}
                                                    >
                                                        + Add a new school
                                                    </Box>
                                                </li>
                                            );
                                        }
                                        return <li {...props}>{option}</li>;
                                    }}
                                    onChange={(event, newValue) => {
                                        if (newValue === ADD_NEW_SCHOOL_OPTION) {
                                            setIsAddingNewSchool(true);
                                            setFormData((prev) => ({ ...prev, school_name: "", board: "" }));
                                            setErrors((prev) => ({ ...prev, school_name: false, board: false }));
                                            setHelperText((prev) => ({ ...prev, school_name: "", board: "" }));
                                            return;
                                        }
                                        handleChange({
                                            target: { name: "school_name", value: newValue || "" },
                                        });
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Select your school"
                                            name="school_name"
                                            error={!!errors.school_name}
                                            helperText={helperText.school_name}
                                            sx={fieldSx}
                                        />
                                    )}
                                />
                            )}
                        </FormControl>

                        <FormControl fullWidth error={errors.board} sx={formControlSx}>
                            <FormLabel sx={fieldLabelSx}>Board</FormLabel>
                            <Autocomplete
                                fullWidth
                                disabled={isBoardLocked}
                                options={boardOptions}
                                value={formData.board || null}
                                onChange={(event, newValue) =>
                                    handleChange({
                                        target: { name: "board", value: newValue || "" },
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Board"
                                        name="board"
                                        disabled={isBoardLocked}
                                        error={!!errors.board}
                                        helperText={helperText.board}
                                        sx={fieldSx}
                                    />
                                )}
                            />
                        </FormControl>

                        <FormControl fullWidth error={errors.class} sx={formControlSx}>
                            <FormLabel sx={fieldLabelSx}>Class</FormLabel>
                            <Autocomplete
                                fullWidth
                                options={STUDENT_CLASS_OPTIONS}
                                value={formData.class || null}
                                onChange={(event, newValue) =>
                                    handleChange({
                                        target: { name: "class", value: newValue || "" },
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select Class"
                                        name="class"
                                        error={!!errors.class}
                                        helperText={helperText.class}
                                        sx={fieldSx}
                                    />
                                )}
                            />
                        </FormControl>

                        {/* Section */}
                        {/* <FormControl fullWidth margin="normal" error={errors.section}>
                            <FormLabel>Section</FormLabel>
                            <CommonTextField
                                name="section"
                                value={formData.section}
                                onChange={handleChange}
                                placeholder="Enter Section"
                                error={errors.section}
                                helperText={helperText.section}
                            />
                        </FormControl> */}

                        {/* Roll Number */}
                        {/* <FormControl fullWidth margin="normal" error={errors.roll_number}>
                            <FormLabel>Roll Number</FormLabel>
                            <CommonTextField
                                name="roll_number"
                                value={formData.roll_number}
                                onChange={handleChange}
                                placeholder="Enter Roll Number"
                                error={errors.roll_number}
                                helperText={helperText.roll_number}
                            />
                        </FormControl> */}

                        <FormControl fullWidth error={errors.birth_date} sx={formControlSx}>
                            <FormLabel sx={fieldLabelSx}>Date of Birth</FormLabel>
                            <TextField
                                type="date"
                                name="birth_date"
                                value={formData.birth_date}
                                onChange={handleChange}
                                error={errors.birth_date}
                                helperText={helperText.birth_date}
                                InputLabelProps={{ shrink: true }}
                                sx={fieldSx}
                            />
                        </FormControl>

                        <FormControl fullWidth error={errors.contact_number} sx={formControlSx}>
                            <FormLabel sx={fieldLabelSx}>Mobile</FormLabel>
                            <MuiTelInput
                                value={formData.contact_number}
                                onChange={handlePhoneChange}
                                defaultCountry="IN"
                                placeholder="Enter Mobile"
                                name="contact_number"
                                error={errors.contact_number}
                                helperText={helperText.contact_number}
                                sx={fieldSx}
                            />
                        </FormControl>

                        <FormControl
                            fullWidth
                            error={errors.address}
                            sx={{ ...formControlSx, gridColumn: { xs: "auto", sm: "1 / -1" } }}
                        >
                            <FormLabel sx={fieldLabelSx}>Address</FormLabel>
                            <CommonTextField
                                name="address"
                                value={formData.address}
                                onChange={handleChange}
                                placeholder="Enter Address"
                                multiline
                                rows={3}
                                error={errors.address}
                                helperText={helperText.address}
                                sx={{
                                    ...fieldSx,
                                    "& .MuiOutlinedInput-root": {
                                        ...fieldSx["& .MuiOutlinedInput-root"],
                                        alignItems: "flex-start",
                                    },
                                }}
                            />
                        </FormControl>
                    </Box>
                )}
            </DialogContent>

            <Divider sx={{ borderColor: PROFILE_COLORS.border }} />
            <DialogActions
                sx={{
                    px: { xs: 2.5, sm: 4 },
                    py: 2.5,
                    justifyContent: "flex-end",
                    gap: 1.25,
                    backgroundColor: PROFILE_COLORS.white,
                }}
            >
                <Button
                    onClick={handleClose}
                    sx={{
                        minWidth: 118,
                        borderRadius: "8px",
                        border: `1px solid ${PROFILE_COLORS.primary}`,
                        color: PROFILE_COLORS.primary,
                        fontFamily: "Poppins",
                        fontSize: 15,
                        fontWeight: 500,
                        textTransform: "none",
                        px: 3,
                        py: 1,
                        "&:hover": {
                            borderColor: PROFILE_COLORS.primary,
                            backgroundColor: "rgba(41, 46, 92, 0.06)",
                        },
                    }}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <CommonButtonActionButton
                    sx={{
                        minWidth: 118,
                        borderRadius: "8px",
                        backgroundColor: PROFILE_COLORS.secondary,
                        color: PROFILE_COLORS.white,
                        fontFamily: "Poppins",
                        fontSize: 15,
                        fontWeight: 600,
                        textTransform: "none",
                        px: 3,
                        py: 1,
                        "&:hover": {
                            backgroundColor: PROFILE_COLORS.secondaryDark,
                        },
                    }}
                    onClick={handleSubmit}
                    disabled={loading || fetching}
                >
                    {loading ? "Saving..." : "Save"}
                </CommonButtonActionButton>
            </DialogActions>
        </Dialog>
    );
};

StudentProfileUpdate.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default StudentProfileUpdate;
